import { Injectable, NgZone, inject } from '@angular/core';

export interface StickyHeaderOptions {
  enabled?: boolean;
  offset?: number;
}

interface StickyEntry {
  table: HTMLTableElement;
  thead: HTMLTableSectionElement;
  wrapper: HTMLElement | null;
  clipped: HTMLElement[];
  stuck: boolean;
  headHeight: number;
}

interface ClipState {
  count: number;
  saved: Record<string, [string, string]>;
}

interface HScroll {
  wrapper: HTMLElement;
  proxy: HTMLDivElement;
  spacer: HTMLDivElement;
  x: number;
  pointerId: number | null;
  startX: number;
  startLeft: number;
  touchAction: string;
}

@Injectable({ providedIn: 'root' })
export class StickyTableHeaderService {
  private static readonly HEADER_CLASS = 'app-sticky-header';
  private static readonly STUCK_CLASS = 'app-sticky-stuck';
  private static readonly HSCROLL_CLASS = 'app-sticky-hscroll';
  private static readonly OFFSET_VAR = '--app-sticky-offset';
  private static readonly HEIGHT_VAR = '--app-sticky-h';
  private static readonly CONTROL_ATTR = 'data-sticky-header';
  private static readonly CLIP_PROPS = ['overflow-x', 'overflow-y', 'min-width', 'min-height'];
  private static readonly VERTICAL_INTENT = /(^|\s)(overflow-y-(auto|scroll)|h-(\d|\[|screen))/;
  private static readonly CONTENT_SIZED_SCROLLERS = '.mat-mdc-tab-body-wrapper, .mat-mdc-tab-body, .mat-mdc-tab-body-content';
  private static readonly EXCLUDE_SELECTOR = ['.app-kv-table', 'table table', '.rich-html table', '.cke_editable table', '[contenteditable] table', '.mat-calendar-table'].join(',');
  private static readonly PINNED_SELECTOR = 'th.sticky, td.sticky';
  private static readonly TOP_BAR_SELECTOR = '.app-sticky-top';
  private static readonly DETAIL_HOST_PATTERN = /^app-(detail|view)-|-(detail|details|summary)$|-details?-dialog$|dashboard|profile|disclaimer|kyc/;
  private static readonly DETAIL_HOSTS = [
    'app-active-work-orders',
    'app-active-surveys',
    'app-budgeted',
    'app-related-budgeted-rfps',
    'app-reserved-fund-expense',
    'app-sla-evaluations',
    'app-comparative-analysis-list',
    'app-eservice-payment',
    'app-work-order-actions',
    'app-violation-notice-resolve',
    'app-resident-information-config-dialog',
    'app-avenue',
    'app-cyber-source',
    'app-etisalat',
    'app-n-genius',
    'app-noor-bank',
    'app-pay-fort',
    'app-pay-tab',
    'app-tap',
    'app-total-processing',
  ];
  private static readonly PASSTHROUGH_HOSTS = ['app-loader-overlay'];
  private static readonly LIST_HOSTS = ['app-owners-details', 'app-units-details', 'app-unit-summary', 'app-all-owner-kyc'];

  private readonly ngZone = inject(NgZone);

  private readonly entries = new Map<HTMLTableElement, StickyEntry>();
  private readonly options = new WeakMap<HTMLTableElement, StickyHeaderOptions>();
  private readonly clips = new Map<HTMLElement, ClipState>();
  private readonly hscrolls = new Map<HTMLElement, HScroll>();

  private resizeObserver?: ResizeObserver;
  private started = false;
  private resizeFrame = 0;

  init(): void {
    if (this.started || typeof document === 'undefined' || typeof ResizeObserver === 'undefined') return;
    this.started = true;

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((records) => this.onResize(records));
      this.processAll(document.body);

      new MutationObserver((mutations) => this.onMutations(mutations)).observe(document.body, { childList: true, subtree: true });

      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onWindowResize, { passive: true });
    });
  }

  configure(table: HTMLTableElement, options: StickyHeaderOptions | null): void {
    if (options) this.options.set(table, options);
    else this.options.delete(table);
    if (!this.started) return;

    const entry = this.entries.get(table);
    if (options?.enabled === false) {
      if (entry) this.release(table);
      return;
    }
    if (entry) this.resolve(entry);
    else this.register(table);
  }

  refresh(): void {
    this.entries.forEach((entry) => (entry.table.isConnected ? this.resolve(entry) : this.release(entry.table)));
  }

  private processAll(root: ParentNode): void {
    root.querySelectorAll<HTMLTableElement>('table').forEach((table) => this.register(table));
  }

  private onMutations(mutations: MutationRecord[]): void {
    const added = new Set<HTMLTableElement>();
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as HTMLElement;
        const own = el.closest('table');
        if (own) added.add(own as HTMLTableElement);
        el.querySelectorAll?.<HTMLTableElement>('table').forEach((t) => added.add(t));
      }
      for (const node of Array.from(mutation.removedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as HTMLElement;
        if (el.tagName === 'TABLE' && this.entries.has(el as HTMLTableElement)) this.release(el as HTMLTableElement);
        el.querySelectorAll?.<HTMLTableElement>('table').forEach((t) => this.entries.has(t) && this.release(t));
      }
    }
    added.forEach((table) => this.register(table));
  }

  private isEligible(table: HTMLTableElement): boolean {
    const opts = this.options.get(table);
    if (opts?.enabled === false) return false;
    if (!table.querySelector(':scope > thead')) return false;

    const control = table.closest(`[${StickyTableHeaderService.CONTROL_ATTR}]`)?.getAttribute(StickyTableHeaderService.CONTROL_ATTR);
    if (control === 'off') return false;
    if (control === 'on' || opts?.enabled === true) return true;

    const host = this.findHost(table);
    if (host && !StickyTableHeaderService.LIST_HOSTS.includes(host) && (StickyTableHeaderService.DETAIL_HOST_PATTERN.test(host) || StickyTableHeaderService.DETAIL_HOSTS.includes(host))) return false;

    return !table.matches(StickyTableHeaderService.EXCLUDE_SELECTOR);
  }

  private findHost(table: HTMLTableElement): string | null {
    let el = table.parentElement;
    while (el && el !== document.body) {
      const tag = el.tagName.toLowerCase();
      if (tag.startsWith('app-') && !StickyTableHeaderService.PASSTHROUGH_HOSTS.includes(tag)) return tag;
      el = el.parentElement;
    }
    return null;
  }

  private register(table: HTMLTableElement): void {
    if (this.entries.has(table) || !table.isConnected || !this.isEligible(table)) return;
    const thead = table.querySelector<HTMLTableSectionElement>(':scope > thead')!;

    const entry: StickyEntry = { table, thead, wrapper: null, clipped: [], stuck: false, headHeight: 0 };
    this.entries.set(table, entry);
    thead.classList.add(StickyTableHeaderService.HEADER_CLASS);
    this.resolve(entry);

    this.resizeObserver!.observe(table);
    this.resizeObserver!.observe(thead);
  }

  private release(table: HTMLTableElement): void {
    const entry = this.entries.get(table);
    if (!entry) return;
    this.entries.delete(table);

    this.resizeObserver?.unobserve(entry.table);
    this.resizeObserver?.unobserve(entry.thead);
    this.unclip(entry);

    entry.thead.classList.remove(StickyTableHeaderService.HEADER_CLASS, StickyTableHeaderService.STUCK_CLASS);
    entry.thead.style.removeProperty(StickyTableHeaderService.OFFSET_VAR);
    table.style.removeProperty(StickyTableHeaderService.HEIGHT_VAR);
    table.querySelectorAll<HTMLElement>(`:scope > tbody ${StickyTableHeaderService.PINNED_SELECTOR.replace(', ', ', :scope > tbody ')}`).forEach((cell) => cell.style.removeProperty('z-index'));
  }

  private resolve(entry: StickyEntry): void {
    const opts = this.options.get(entry.table);

    const headHeight = entry.thead.getBoundingClientRect().height;
    const wrapper = this.findWrapper(entry.table);
    if (wrapper !== entry.wrapper) {
      this.unclip(entry);
      entry.wrapper = wrapper;
    }

    if (wrapper && !this.isRealScroller(wrapper)) this.clip(entry);
    else this.unclip(entry);

    if (entry.wrapper && this.clips.has(entry.wrapper)) this.syncHScroll(entry.wrapper);

    entry.table.querySelectorAll<HTMLElement>(`:scope > tbody ${StickyTableHeaderService.PINNED_SELECTOR.replace(', ', ', :scope > tbody ')}`).forEach((cell) => {
      if (cell.style.zIndex !== '1') cell.style.setProperty('z-index', '1', 'important');
    });

    const scroller = entry.wrapper && !entry.clipped.length ? entry.wrapper : this.findScroller(entry.clipped.length ? entry.clipped[entry.clipped.length - 1] : entry.table);
    const top = scroller ? -(parseFloat(getComputedStyle(scroller).paddingTop) || 0) : this.viewportTopOffset();
    const offset = (opts?.offset ?? 0) + top;
    if (offset) entry.thead.style.setProperty(StickyTableHeaderService.OFFSET_VAR, `${offset}px`);
    else entry.thead.style.removeProperty(StickyTableHeaderService.OFFSET_VAR);

    const reserved = headHeight + Math.max(offset, 0);
    if (reserved !== entry.headHeight) {
      entry.headHeight = reserved;
      entry.table.style.setProperty(StickyTableHeaderService.HEIGHT_VAR, `${reserved}px`);
    }

    this.updateStuck(entry);
  }

  private viewportTopOffset(): number {
    let total = 0;
    document.querySelectorAll<HTMLElement>(StickyTableHeaderService.TOP_BAR_SELECTOR).forEach((bar) => {
      const position = getComputedStyle(bar).position;
      if (position === 'sticky' || position === 'fixed') total += bar.getBoundingClientRect().height;
    });
    return total;
  }

  private findScroller(from: HTMLElement): HTMLElement | null {
    let el = from.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      if (this.isPotentialScroller(el) && !el.matches(StickyTableHeaderService.CONTENT_SIZED_SCROLLERS)) return el;
      el = el.parentElement;
    }
    return null;
  }

  private findWrapper(table: HTMLTableElement): HTMLElement | null {
    let el = table.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      if (this.clips.has(el) || this.isOverflowContainer(el)) return el;
      el = el.parentElement;
    }
    return null;
  }

  private isOverflowContainer(el: HTMLElement): boolean {
    const cs = getComputedStyle(el);
    return !this.isNonScrolling(cs.overflowX) || !this.isNonScrolling(cs.overflowY);
  }

  private isNonScrolling(overflow: string): boolean {
    return overflow === 'visible' || overflow === 'clip';
  }

  private isPotentialScroller(el: HTMLElement): boolean {
    const overflowY = getComputedStyle(el).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  }

  private isRealScroller(wrapper: HTMLElement): boolean {
    if (wrapper.scrollHeight > wrapper.clientHeight + 1) return true;
    if (this.clips.has(wrapper)) return false;
    if (getComputedStyle(wrapper).maxHeight !== 'none') return true;
    if (wrapper.style.overflowY || wrapper.style.overflow) return this.isPotentialScroller(wrapper);
    return StickyTableHeaderService.VERTICAL_INTENT.test(wrapper.className);
  }

  private clip(entry: StickyEntry): void {
    if (entry.clipped.length) return;
    const wrapper = entry.wrapper!;
    const chain: HTMLElement[] = [];

    let el = entry.table.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const contentSized = el.matches(StickyTableHeaderService.CONTENT_SIZED_SCROLLERS);
      if (el !== wrapper && !contentSized && this.isPotentialScroller(el)) break;
      if (this.clips.has(el) || this.isOverflowContainer(el)) {
        if (el !== wrapper && !contentSized && !this.clips.has(el) && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)) return;
        chain.push(el);
      }
      el = el.parentElement;
    }

    chain.forEach((target) => this.addClip(target));
    entry.clipped = chain;
  }

  private unclip(entry: StickyEntry): void {
    if (!entry.clipped.length) return;
    entry.clipped.forEach((target) => this.removeClip(target));
    entry.clipped = [];
  }

  private addClip(el: HTMLElement): void {
    const state = this.clips.get(el);
    if (state) {
      state.count++;
      return;
    }
    const saved: Record<string, [string, string]> = {};
    for (const prop of StickyTableHeaderService.CLIP_PROPS) saved[prop] = [el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)];
    this.clips.set(el, { count: 1, saved });
    el.style.setProperty('overflow-x', 'clip', 'important');
    el.style.setProperty('overflow-y', 'clip', 'important');
    el.style.setProperty('min-width', '0', 'important');
    el.style.setProperty('min-height', '0', 'important');
  }

  private removeClip(el: HTMLElement): void {
    const state = this.clips.get(el);
    if (!state) return;
    if (--state.count > 0) return;
    this.clips.delete(el);
    this.destroyHScroll(el);
    for (const prop of StickyTableHeaderService.CLIP_PROPS) {
      const [value, priority] = state.saved[prop];
      if (value) el.style.setProperty(prop, value, priority);
      else el.style.removeProperty(prop);
    }
  }

  private tablesIn(wrapper: HTMLElement): HTMLTableElement[] {
    return Array.from(this.entries.values())
      .filter((entry) => entry.wrapper === wrapper)
      .map((entry) => entry.table);
  }

  private syncHScroll(wrapper: HTMLElement): void {
    const tables = this.tablesIn(wrapper);
    const width = Math.ceil(Math.max(0, ...tables.map((table) => table.getBoundingClientRect().width)));
    const overflow = width - wrapper.clientWidth;

    if (overflow <= 1) {
      this.destroyHScroll(wrapper);
      return;
    }

    let state = this.hscrolls.get(wrapper);
    if (!state) state = this.createHScroll(wrapper);
    state.spacer.style.width = `${width}px`;
    state.x = Math.min(state.x, overflow);
    if (state.proxy.scrollLeft !== state.x) state.proxy.scrollLeft = state.x;
    this.applyHScroll(state);
  }

  private createHScroll(wrapper: HTMLElement): HScroll {
    const proxy = document.createElement('div');
    proxy.className = StickyTableHeaderService.HSCROLL_CLASS;
    wrapper.className
      .split(/\s+/)
      .filter((token) => token.includes('webkit-scrollbar'))
      .forEach((token) => proxy.classList.add(token));
    const spacer = document.createElement('div');
    proxy.appendChild(spacer);
    wrapper.insertAdjacentElement('afterend', proxy);

    const state: HScroll = { wrapper, proxy, spacer, x: 0, pointerId: null, startX: 0, startLeft: 0, touchAction: wrapper.style.touchAction };
    this.hscrolls.set(wrapper, state);
    wrapper.style.touchAction = 'pan-y pinch-zoom';

    proxy.addEventListener(
      'scroll',
      () => {
        state.x = proxy.scrollLeft;
        this.applyHScroll(state);
      },
      { passive: true }
    );
    wrapper.addEventListener('wheel', this.onWheel, { passive: true });
    wrapper.addEventListener('pointerdown', this.onPointerDown);
    wrapper.addEventListener('pointermove', this.onPointerMove);
    wrapper.addEventListener('pointerup', this.onPointerEnd);
    wrapper.addEventListener('pointercancel', this.onPointerEnd);
    wrapper.addEventListener('focusin', this.onFocusIn);
    return state;
  }

  private destroyHScroll(wrapper: HTMLElement): void {
    const state = this.hscrolls.get(wrapper);
    if (!state) return;
    this.hscrolls.delete(wrapper);
    state.proxy.remove();
    wrapper.style.touchAction = state.touchAction;
    wrapper.removeEventListener('wheel', this.onWheel);
    wrapper.removeEventListener('pointerdown', this.onPointerDown);
    wrapper.removeEventListener('pointermove', this.onPointerMove);
    wrapper.removeEventListener('pointerup', this.onPointerEnd);
    wrapper.removeEventListener('pointercancel', this.onPointerEnd);
    wrapper.removeEventListener('focusin', this.onFocusIn);
    this.tablesIn(wrapper).forEach((table) => {
      table.style.removeProperty('transform');
      table.querySelectorAll<HTMLElement>(StickyTableHeaderService.PINNED_SELECTOR).forEach((cell) => cell.style.removeProperty('transform'));
    });
  }

  private applyHScroll(state: HScroll): void {
    const value = state.x > 0 ? `translateX(${-state.x}px)` : '';
    const pinned = state.x > 0 ? `translateX(${state.x}px)` : '';
    this.tablesIn(state.wrapper).forEach((table) => {
      if (table.style.transform !== value) table.style.transform = value;
      table.querySelectorAll<HTMLElement>(StickyTableHeaderService.PINNED_SELECTOR).forEach((cell) => {
        if (cell.style.transform !== pinned) cell.style.transform = pinned;
      });
    });
  }

  private onWheel = (event: WheelEvent): void => {
    const state = this.hscrolls.get(event.currentTarget as HTMLElement);
    if (!state) return;
    const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
    if (delta) state.proxy.scrollLeft += delta;
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') return;
    const state = this.hscrolls.get(event.currentTarget as HTMLElement);
    if (!state) return;
    state.pointerId = event.pointerId;
    state.startX = event.clientX;
    state.startLeft = state.proxy.scrollLeft;
  };

  private onPointerMove = (event: PointerEvent): void => {
    const state = this.hscrolls.get(event.currentTarget as HTMLElement);
    if (!state || state.pointerId !== event.pointerId) return;
    state.proxy.scrollLeft = state.startLeft - (event.clientX - state.startX);
  };

  private onPointerEnd = (event: PointerEvent): void => {
    const state = this.hscrolls.get(event.currentTarget as HTMLElement);
    if (state && state.pointerId === event.pointerId) state.pointerId = null;
  };

  private onFocusIn = (event: FocusEvent): void => {
    const state = this.hscrolls.get(event.currentTarget as HTMLElement);
    const target = event.target;
    if (!state || !(target instanceof HTMLElement)) return;
    const box = state.wrapper.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    if (rect.left < box.left) state.proxy.scrollLeft -= box.left - rect.left + 16;
    else if (rect.right > box.right) state.proxy.scrollLeft += rect.right - box.right + 16;
  };

  private updateStuck(entry: StickyEntry): void {
    const stuck = entry.thead.getBoundingClientRect().top - entry.table.getBoundingClientRect().top > 3;
    if (stuck !== entry.stuck) {
      entry.stuck = stuck;
      entry.thead.classList.toggle(StickyTableHeaderService.STUCK_CLASS, stuck);
    }
  }

  private onScroll = (event: Event): void => {
    const target = event.target;
    const scope = target instanceof HTMLElement ? target : null;
    for (const entry of this.entries.values()) {
      if (!scope || scope.contains(entry.table)) this.updateStuck(entry);
    }
  };

  private onResize(records: ResizeObserverEntry[]): void {
    const touched = new Set<StickyEntry>();
    for (const record of records) {
      const el = record.target as HTMLElement;
      const table = (el.tagName === 'TABLE' ? el : el.parentElement) as HTMLTableElement | null;
      const entry = table ? this.entries.get(table) : undefined;
      if (entry) touched.add(entry);
    }
    touched.forEach((entry) => (entry.table.isConnected ? this.resolve(entry) : this.release(entry.table)));
  }

  private onWindowResize = (): void => {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.refresh());
  };
}
